import React, { useState, useEffect } from "react";
import { Books, CaretRight, CaretDown, CircleNotch } from "phosphor-react";
import LessonBrainIcon from "./LessonBrainIcon";
import { MotionHighlight, MotionHighlightItem } from "../shared/motion-highlight";

// Helper to convert index to alphabet (0 -> a, 1 -> b, ..., 25 -> z, 26 -> aa, etc.)
const getAlphabetLabel = (index) => {
  if (index < 26) {
    return String.fromCharCode(97 + index);
  }
  const firstLetter = Math.floor(index / 26) - 1;
  const secondLetter = index % 26;
  return String.fromCharCode(97 + firstLetter) + String.fromCharCode(97 + secondLetter);
};

const parseTitle = (title) => {
  if (!title || typeof title !== "string") {
    return { german: title || "Untitled", indonesian: "" };
  }
  const match = title.match(/^(.+?)\s*\((.+?)\)$/);
  if (match) {
    return {
      german: match[1].trim(),
      indonesian: `(${match[2].trim()})`,
    };
  }
  return { german: title, indonesian: "" };
};

const LessonsTreeTable = ({
  lessons = [],
  skillsCache = {},
  expandedLessonIds = new Set(),
  loadingLessonId = null,
  onLessonClick,
  onSkillSelect,
  onToggleLesson,
  loading = false,
  selectedSkillId,
  activeLessonId,
  initialCollapsed = false,
  onQuickPracticeStart,
  onSpeakingPracticeStart,
  onWritingPracticeStart,
  quickStats = { level: null, progress: null },
  writingStats = { level: null, progress: null },
  speakingStats = { level: null, progress: null },
  loadingStats = false,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);
  const [hoveredId, setHoveredId] = useState(null);

  const handleLessonClick = (lessonId, e) => {
    e?.stopPropagation();
    if (onLessonClick) onLessonClick(lessonId);
    if (onToggleLesson && !expandedLessonIds.has(lessonId)) {
      onToggleLesson(lessonId);
    }
  };

  const handleExpandClick = (lessonId, e) => {
    e?.stopPropagation();
    if (onToggleLesson) onToggleLesson(lessonId);
  };

  const handleSkillClick = (skillId, lessonId, e) => {
    e?.stopPropagation();
    if (onSkillSelect) onSkillSelect(skillId, lessonId);
  };

  useEffect(() => {
    setIsCollapsed(initialCollapsed);
  }, [initialCollapsed]);

  return (
    <div className={`w-[340px] min-w-[340px] bg-white dark:bg-[var(--card-bg)] rounded-[10px] overflow-visible shadow-[0_4px_16px_rgba(0,0,0,0.12)] dark:shadow-[var(--shadow-sm)] flex-shrink-0 transition-all duration-300 relative z-[1] max-md:w-[calc(100%-32px)] max-md:min-w-0 max-md:mx-auto max-md:rounded-xl max-md:shadow-[0_4px_20px_rgba(0,0,0,0.1)] dark:max-md:shadow-[var(--shadow-sm)] ${isCollapsed ? "!w-9 !h-9 !min-w-[36px] !rounded-full !flex !items-center !justify-center max-md:!w-8 max-md:!h-8 max-md:!min-w-8" : ""}`}>
      {isCollapsed ? (
        <div
          className="w-full h-full flex items-center justify-center cursor-pointer text-[#12766d] dark:text-[var(--accent-teal)] transition-transform duration-200 hover:scale-105"
          onClick={() => setIsCollapsed(false)}
        >
          <Books size={18} weight="duotone" />
        </div>
      ) : (
        <>
          <div className="bg-gradient-to-br from-[#12766d] via-[#236463] to-[#1d4746] dark:from-[#0d4f49] dark:via-[#153938] dark:to-[#0f2928] px-4 py-3 flex items-center gap-2 text-white relative rounded-t-[10px] max-md:rounded-t-xl">
            <Books size={18} weight="regular" />
            <span className="text-[14px] font-bold flex-1 max-md:text-[13px]">Reading List</span>
            <CaretRight
              size={20}
              weight="bold"
              className="cursor-pointer transition-[transform,opacity] duration-200 opacity-80 hover:opacity-100 hover:scale-110 max-md:hidden"
              onClick={() => setIsCollapsed(true)}
            />
          </div>
          <MotionHighlight
            mode="parent"
            controlledItems={true}
            value={hoveredId}
            className="bg-[rgba(18,118,109,0.08)] dark:bg-[rgba(20,184,166,0.08)] rounded-lg pointer-events-none"
            containerClassName="p-3 flex flex-col gap-2 bg-[var(--bg-primary)] overflow-visible max-md:px-3 max-md:py-2 max-md:gap-1.5"
            transition={{ type: "spring", stiffness: 380, damping: 38 }}
          >
            {loading ? (
              <div className="flex flex-col items-center justify-center py-10 px-5 gap-3">
                <CircleNotch size={24} weight="bold" className="text-[#12766d] animate-spin" />
                <span className="text-[13px] text-[var(--text-secondary)]">Loading lessons...</span>
              </div>
            ) : (
              lessons.map((lesson) => {
                const lessonId = lesson.id || lesson._id;
                const isLessonActive = activeLessonId === lessonId;
                const isExpanded = expandedLessonIds.has(lessonId);
                const cached = skillsCache[lessonId];
                const skills = cached?.skills || [];
                const skillsLoading = loadingLessonId === lessonId;

                const titleParts = parseTitle(lesson.title || lesson.name || "Untitled Lesson");
                const lessonQuickCompletion = lesson["quick-completion"] ?? null;
                const lessonWritingCompletion = lesson["writing-completion"] ?? null;
                const lessonSpeakingCompletion = lesson["speaking-completion"] ?? null;
                const lessonAllCompletion = lesson["all-completion"] ?? null;
                const calculatedAllCompletion =
                  lessonAllCompletion === null &&
                  (lessonQuickCompletion !== null ||
                    lessonWritingCompletion !== null ||
                    lessonSpeakingCompletion !== null)
                    ? Math.round(
                        ((lessonQuickCompletion ?? 0) +
                          (lessonWritingCompletion ?? 0) +
                          (lessonSpeakingCompletion ?? 0)) /
                          3
                      )
                    : lessonAllCompletion;

                const lessonQuickStatsFinal =
                  lessonQuickCompletion !== null
                    ? { level: quickStats.level ?? 0, progress: lessonQuickCompletion }
                    : quickStats;
                const lessonWritingStatsFinal =
                  lessonWritingCompletion !== null
                    ? { level: writingStats.level ?? 0, progress: lessonWritingCompletion }
                    : writingStats;
                const lessonSpeakingStatsFinal =
                  lessonSpeakingCompletion !== null
                    ? { level: speakingStats.level ?? 0, progress: lessonSpeakingCompletion }
                    : speakingStats;

                const isHighProgress =
                  calculatedAllCompletion !== null && calculatedAllCompletion >= 80;

                return (
                  <div key={lessonId} className="flex flex-col gap-0">
                    {/* Lesson row */}
                    <MotionHighlightItem value={`lesson-${lessonId}`} asChild>
                      <button
                        type="button"
                        className={`lesson-tree-card bg-transparent rounded-lg p-[10px_12px] flex flex-col gap-2 cursor-pointer transition-all duration-200 relative w-full text-left font-[inherit] text-[inherit] text-[var(--text-primary)] [-webkit-tap-highlight-color:transparent] outline-none appearance-none [-webkit-appearance:none] ${isLessonActive ? "active !bg-[rgba(252,179,25,0.12)]" : ""} ${isHighProgress ? "high-progress" : ""}`}
                        onClick={(e) => handleLessonClick(lessonId, e)}
                        onMouseEnter={() => setHoveredId(`lesson-${lessonId}`)}
                        onMouseLeave={() => setHoveredId(null)}
                        aria-label={`Select lesson: ${titleParts.german}`}
                      >
                        <div className="flex items-center gap-2 w-full">
                          <button
                            type="button"
                            className="w-6 h-6 p-0 flex items-center justify-center bg-transparent border-none cursor-pointer text-[var(--text-secondary)] flex-shrink-0 transition-colors duration-200 hover:text-[var(--text-primary)]"
                            onClick={(e) => handleExpandClick(lessonId, e)}
                            aria-label={isExpanded ? "Collapse" : "Expand"}
                          >
                            {isExpanded ? (
                              <CaretDown size={16} weight="bold" />
                            ) : (
                              <CaretRight size={16} weight="bold" />
                            )}
                          </button>
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[13px] font-semibold flex-shrink-0 transition-all duration-200 border-2 max-md:w-[26px] max-md:h-[26px] max-md:text-[11px] ${isLessonActive ? "bg-[#fcb319] border-[#fcb319] !text-[#1d4746]" : "bg-[var(--progress-bg)] text-[var(--text-primary)] border-[var(--text-tertiary)]"}`}>
                            {lesson.order ?? lessonId}
                          </div>
                          <div className="flex-1 flex flex-col gap-0.5 min-w-0">
                            <div className="text-[13px] font-normal text-[var(--text-primary)] leading-[1.4] max-md:text-[12px]">{titleParts.german}</div>
                            {titleParts.indonesian && (
                              <div className="text-[12px] font-normal text-[var(--text-secondary)] leading-[1.4] max-md:text-[11px]">
                                {titleParts.indonesian}
                              </div>
                            )}
                          </div>
                          <LessonBrainIcon
                            lessonId={lessonId}
                            onQuickPracticeStart={onQuickPracticeStart}
                            onWritingPracticeStart={onWritingPracticeStart}
                            onSpeakingPracticeStart={onSpeakingPracticeStart}
                            onLessonClick={(id) => onLessonClick?.(id)}
                            isActive={isLessonActive}
                            quickStats={lessonQuickStatsFinal}
                            writingStats={lessonWritingStatsFinal}
                            speakingStats={lessonSpeakingStatsFinal}
                            loadingStats={loadingStats}
                          />
                        </div>
                        {calculatedAllCompletion !== null && (
                          <div className={`lesson-tree-progress-bar-container flex items-center gap-2 mt-1 ml-[60px] pointer-events-none${calculatedAllCompletion >= 80 ? " high-progress" : ""}`}>
                            <div className="lesson-tree-progress-bar relative flex-1 h-[6px] bg-[rgba(18,118,109,0.1)] rounded-full overflow-visible max-w-[calc(100%-50px)]">
                              <div
                                className="lesson-tree-progress-indicator absolute top-0 bottom-0 w-px bg-[rgba(18,118,109,0.4)] z-[2] -translate-x-1/2"
                                style={{ left: "80%" }}
                              />
                              <div
                                className="lesson-tree-progress-fill relative h-full bg-gradient-to-r from-[#12766d] via-[#236463] to-[#1d4746] rounded-full transition-[width_0.3s_ease] z-[1]"
                                style={{
                                  width: `${Math.min(100, Math.max(0, calculatedAllCompletion))}%`,
                                }}
                              />
                            </div>
                            <span className="lesson-tree-progress-text text-[11px] font-semibold text-[var(--accent-teal)] min-w-[35px] text-right flex-shrink-0">
                              {Math.round(Math.min(100, Math.max(0, calculatedAllCompletion)))}%
                            </span>
                          </div>
                        )}
                      </button>
                    </MotionHighlightItem>

                    {/* Skills (children) */}
                    {isExpanded && (
                      <div className="lesson-tree-skills relative ml-7 mt-1 pl-4">
                        <div className="lesson-tree-skills-list flex flex-col gap-1 relative">
                          {skillsLoading && skills.length === 0 ? (
                            <div className="text-[12px] text-[var(--text-tertiary)] py-2">Loading skills...</div>
                          ) : skills.length === 0 ? (
                            <div className="text-[12px] text-[var(--text-tertiary)] py-2">No skills yet</div>
                          ) : (
                            skills.map((skill, skillIndex) => {
                              const skillId = skill.id || skill._id || `skill-${skillIndex}`;
                              const title =
                                skill.name || skill.title || skill["skill-name"] || "Untitled";
                              const subtitle =
                                skill["lang-type"] ||
                                skill.topic ||
                                skill.category ||
                                skill.type ||
                                "";
                              const isSkillActive = String(skillId) === String(selectedSkillId);
                              const alphabetLabel = getAlphabetLabel(skillIndex);

                              return (
                                <MotionHighlightItem key={skillId} value={`skill-${skillId}`} asChild>
                                  <button
                                    type="button"
                                    className={`lesson-tree-skill-item relative rounded-lg p-[8px_12px] flex items-center gap-2.5 cursor-pointer transition-all duration-200 text-left w-full font-[inherit] text-[inherit] before:content-[''] before:absolute before:left-[-11px] before:top-0 before:h-[calc(100%_+_4px)] before:w-0.5 before:bg-[rgba(18,118,109,0.3)] dark:before:bg-[rgba(20,184,166,0.2)] last:before:!h-1/2 after:content-[''] after:absolute after:top-1/2 after:left-[-11px] after:w-[11px] after:h-px after:bg-[rgba(18,118,109,0.35)] dark:after:bg-[rgba(20,184,166,0.25)] after:-translate-y-1/2 max-md:p-[8px_10px] ${isSkillActive ? "active !bg-[rgba(252,179,25,0.15)]" : ""}`}
                                    onClick={(e) => handleSkillClick(skillId, lessonId, e)}
                                    onMouseEnter={() => setHoveredId(`skill-${skillId}`)}
                                    onMouseLeave={() => setHoveredId(null)}
                                  >
                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 transition-all duration-200 border-2 max-md:w-[22px] max-md:h-[22px] max-md:text-[10px] ${isSkillActive ? "bg-[#fcb319] border-[#fcb319] !text-[#1d4746]" : "bg-[var(--progress-bg)] text-[var(--text-primary)] border-[var(--text-tertiary)]"}`}>
                                      {alphabetLabel}
                                    </span>
                                    <div className="flex-1 flex flex-col gap-0.5 min-w-0">
                                      <span className="text-[13px] font-normal text-[var(--text-primary)] leading-[1.4] max-md:text-[12px]">{title}</span>
                                      {subtitle && (
                                        <span className="text-[12px] font-normal text-[var(--text-secondary)] leading-[1.4] max-md:text-[11px]">
                                          {subtitle}
                                        </span>
                                      )}
                                    </div>
                                    <span className="text-[16px] text-[#b0b0b0] flex-shrink-0">›</span>
                                  </button>
                                </MotionHighlightItem>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </MotionHighlight>
        </>
      )}
    </div>
  );
};

export default LessonsTreeTable;
